import React from "react";
import { Star } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export function Money({ amount, className = "" }) {
  return <span className={className}>₹{Number(amount || 0).toLocaleString("en-IN")}</span>;
}

export function StarRating({ value = 0, size = 14, showValue = false }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= Math.round(value) ? "fill-amber text-amber" : "text-gray-300"} />
      ))}
      {showValue && <span className="ml-1 text-sm font-semibold text-navy">{Number(value).toFixed(1)}</span>}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "bg-navy", testid }) {
  return (
    <div className="bg-white rounded-md border border-border p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        {Icon && (
          <span className={`h-9 w-9 rounded-md ${accent} text-white flex items-center justify-center`}>
            <Icon size={18} />
          </span>
        )}
      </div>
      <div className="mt-3 font-heading text-3xl font-black text-navy">{value}</div>
    </div>
  );
}

export function Loading({ rows = 6 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-border p-4">
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-4 w-3/4 mt-4" />
          <Skeleton className="h-4 w-1/2 mt-2" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
      {Icon && <Icon size={48} className="text-gray-300 mb-4" />}
      <h3 className="font-heading text-xl font-semibold text-navy">{title}</h3>
      {subtitle && <p className="text-muted-foreground mt-1 max-w-md">{subtitle}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Badge({ children, tone = "navy" }) {
  const tones = {
    navy: "bg-navy/10 text-navy",
    amber: "bg-yellow-100 text-yellow-800",
    crimson: "bg-crimson/10 text-crimson",
    green: "bg-emerald-100 text-emerald-700",
    gray: "bg-gray-100 text-gray-600",
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${tones[tone] || tones.gray}`}>{children}</span>;
}

export function statusTone(status) {
  const map = {
    published: "green", approved: "green", active: "green", paid: "green", available: "green", evaluated: "green",
    draft: "gray", pending: "amber", submitted: "amber", under_review: "amber", scheduled: "amber", new: "amber", pending_review: "amber",
    rejected: "crimson", suspended: "crimson", withdrawn: "gray", unpublished: "gray",
    requested: "amber", processing: "amber", payment_pending: "amber", documents_required: "amber", failed: "crimson", reversed: "crimson",
  };
  return map[status] || "gray";
}
